import { supabase } from '@/integrations/supabase/client';

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface ErrorLogEntry {
  message: string;
  severity: ErrorSeverity;
  context?: string;
  stack?: string;
  metadata?: Record<string, unknown>;
}

class ErrorLogger {
  private queue: ErrorLogEntry[] = [];
  private isProcessing = false;
  private userId: string | null = null;

  setUserId(userId: string | null) {
    this.userId = userId;
  }

  async log(entry: ErrorLogEntry): Promise<void> {
    // Console log for development
    const logMethod = entry.severity === 'error' || entry.severity === 'critical' 
      ? console.error 
      : entry.severity === 'warning' 
        ? console.warn 
        : console.log;
    
    logMethod(`[${entry.severity.toUpperCase()}] ${entry.context || 'App'}: ${entry.message}`, entry.metadata || '');

    // Add to queue for batch processing
    this.queue.push(entry);
    
    // Process queue if not already processing
    if (!this.isProcessing) {
      await this.processQueue();
    }
  }

  private async processQueue(): Promise<void> {
    if (this.queue.length === 0 || !this.userId) return;
    
    this.isProcessing = true;
    
    try {
      const entries = [...this.queue];
      this.queue = [];

      // Insert logs to database
      const logsToInsert = entries.map(entry => ({
        user_id: this.userId!,
        msg: `[${entry.severity}] ${entry.context || 'App'}: ${entry.message}${entry.stack ? ` | Stack: ${entry.stack.slice(0, 500)}` : ''}`,
        level: entry.severity === 'critical' ? 'error' : entry.severity,
        time: new Date().toLocaleTimeString(),
      }));

      const { error } = await supabase.from('logs').insert(logsToInsert);
      
      if (error) {
        console.error('Failed to save error logs:', error);
        // Re-add failed entries to queue (but limit to prevent memory issues)
        if (this.queue.length < 50) {
          this.queue.push(...entries);
        }
      }
    } catch (err) {
      console.error('Error processing log queue:', err);
    } finally {
      this.isProcessing = false;
    }
  }

  // Convenience methods
  info(message: string, context?: string, metadata?: Record<string, unknown>): void {
    this.log({ message, severity: 'info', context, metadata });
  }

  warn(message: string, context?: string, metadata?: Record<string, unknown>): void {
    this.log({ message, severity: 'warning', context, metadata });
  }

  error(message: string, context?: string, error?: Error, metadata?: Record<string, unknown>): void {
    this.log({ 
      message, 
      severity: 'error', 
      context, 
      stack: error?.stack,
      metadata: { ...metadata, errorName: error?.name }
    });
  }

  critical(message: string, context?: string, error?: Error, metadata?: Record<string, unknown>): void {
    this.log({ 
      message, 
      severity: 'critical', 
      context, 
      stack: error?.stack,
      metadata: { ...metadata, errorName: error?.name }
    });
  }

  // Track import operations
  trackImport(type: 'csv' | 'xml' | 'tally', success: boolean, count: number, errorMessage?: string): void {
    if (success) {
      this.info(`Successfully imported ${count} ${type} entries`, 'Import', { type, count });
    } else {
      this.error(`Failed to import ${type}: ${errorMessage}`, 'Import', undefined, { type, count });
    }
  }

  // Track API calls
  trackApiCall(endpoint: string, success: boolean, duration?: number, errorMessage?: string): void {
    if (success) {
      this.info(`API call to ${endpoint} succeeded`, 'API', { endpoint, duration });
    } else {
      this.warn(`API call to ${endpoint} failed: ${errorMessage}`, 'API', { endpoint, duration });
    }
  }

  // Track user actions
  trackAction(action: string, metadata?: Record<string, unknown>): void {
    this.info(`User action: ${action}`, 'UserAction', metadata);
  }
}

// Singleton instance
export const errorLogger = new ErrorLogger();

// Global error handler
export function setupGlobalErrorHandlers(): void {
  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    errorLogger.critical(
      event.message || 'Unknown error',
      'GlobalError',
      event.error,
      { filename: event.filename, lineno: event.lineno, colno: event.colno }
    );
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const message = event.reason?.message || String(event.reason) || 'Unhandled promise rejection';
    errorLogger.critical(
      message,
      'UnhandledRejection',
      event.reason instanceof Error ? event.reason : undefined
    );
  });
}
